import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Save, StickyNote, Trash2, Loader2 } from 'lucide-react';
import type { ReaderDataProvider, ChapterNote, ReaderThemeMode } from '../types';

interface ChapterNotesProps {
  bookId: string;
  chapterId: string;
  chapterNumber: number;
  chapterTitle: string;
  onClose: () => void;
  theme: ReaderThemeMode;
  dataProvider: ReaderDataProvider;
}

export default function ChapterNotes({
  bookId,
  chapterId,
  chapterNumber,
  chapterTitle,
  onClose,
  theme,
  dataProvider,
}: ChapterNotesProps) {
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [existingNote, setExistingNote] = useState<ChapterNote | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isDark = theme === 'dark';

  // Fetch existing note
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    dataProvider.getChapterNote(chapterId).then((note) => {
      if (cancelled) return;
      setExistingNote(note);
      if (note) {
        setContent(note.content);
        setLastSaved(new Date(note.updatedAt));
      }
      setIsLoading(false);
    }).catch(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => { cancelled = true; };
  }, [chapterId, dataProvider]);

  // Auto-save with debounce
  useEffect(() => {
    // Don't save empty content if there's no existing note
    if (!content.trim() && !existingNote) return;
    // Don't save while still loading
    if (isLoading) return;

    // Clear existing timer
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }

    // Set new timer for auto-save
    saveTimer.current = setTimeout(async () => {
      if (content.trim()) {
        setIsSaving(true);
        try {
          await dataProvider.upsertChapterNote({
            bookId,
            chapterId,
            chapterNumber,
            content,
          });
          setLastSaved(new Date());
        } catch (err) {
          console.error('Failed to save note:', err);
        } finally {
          setIsSaving(false);
        }
      }
    }, 1000);

    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }
    };
  }, [content, bookId, chapterId, chapterNumber, dataProvider, existingNote, isLoading]);

  // Focus textarea on open
  useEffect(() => {
    if (!isLoading) {
      textareaRef.current?.focus();
    }
  }, [isLoading]);

  const handleDelete = async () => {
    if (!existingNote || !confirm('Delete this note?')) return;
    try {
      await dataProvider.deleteChapterNote(existingNote.id);
      setContent('');
      setLastSaved(null);
      setExistingNote(null);
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  const formatLastSaved = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 minute ago';
    if (minutes < 60) return `${minutes} minutes ago`;

    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 100,
        }}
      />

      {/* Panel */}
      <motion.div
        initial={{ x: 360 }}
        animate={{ x: 0 }}
        exit={{ x: 360 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '360px',
          maxWidth: '90vw',
          background: isDark ? '#1a1a2e' : '#ffffff',
          zIndex: 101,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.25rem 1.5rem',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e8e8e8'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <StickyNote
              size={20}
              color="var(--accent)"
              style={{ marginTop: '2px', flexShrink: 0 }}
            />
            <div>
              <span
                style={{
                  fontWeight: 600,
                  color: isDark ? '#fff' : 'var(--text)',
                  fontSize: '1rem',
                  display: 'block',
                }}
              >
                Chapter Notes
              </span>
              <span
                style={{
                  fontSize: '0.8125rem',
                  color: isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-light)',
                  display: 'block',
                  marginTop: '0.25rem',
                }}
              >
                Ch. {chapterNumber}: {chapterTitle.length > 25 ? chapterTitle.slice(0, 25) + '...' : chapterTitle}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '0.5rem',
              cursor: 'pointer',
              color: isDark ? 'rgba(255,255,255,0.7)' : 'var(--text-light)',
              display: 'flex',
              borderRadius: '6px',
              transition: 'background 0.15s',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Notes Editor */}
        <div style={{ flex: 1, padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your notes about this chapter...

What key insights stood out to you?
Any questions you want to remember?
Ideas to apply later?"
            style={{
              flex: 1,
              width: '100%',
              padding: '1rem',
              borderRadius: '12px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e8e8e8'}`,
              background: isDark ? 'rgba(255,255,255,0.05)' : '#fafafa',
              color: isDark ? '#fff' : 'var(--text)',
              fontSize: '0.9375rem',
              lineHeight: 1.7,
              resize: 'none',
              fontFamily: 'inherit',
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--accent)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : '#e8e8e8';
            }}
          />

          {/* Status bar */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '1rem',
              fontSize: '0.8125rem',
              color: isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-light)',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              {isSaving ? (
                <>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  Saving...
                </>
              ) : lastSaved ? (
                <>
                  <Save size={14} />
                  Saved {formatLastSaved(lastSaved)}
                </>
              ) : (
                'Notes auto-save as you type'
              )}
            </span>

            {existingNote && (
              <button
                onClick={handleDelete}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  fontSize: '0.8125rem',
                  padding: '0.375rem 0.5rem',
                  borderRadius: '6px',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(220, 38, 38, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                }}
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
        </div>

        {/* Word count */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e8e8e8'}`,
            background: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa',
          }}
        >
          <p
            style={{
              fontSize: '0.75rem',
              color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-light)',
              margin: 0,
              textAlign: 'center',
            }}
          >
            {content.trim() ? `${content.trim().split(/\s+/).length} words` : 'Start writing to save your notes'}
          </p>
        </div>
      </motion.div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
